import { ExperimentVersion, ExperimentVersionFieldChange } from "@/http/api-types";
import { predictorTypeName } from "@/lib/predictor-types";
import { formatUTCDateTime } from "@/lib/utils";

export type FormattedHistoryValue = {
  kind: "empty" | "text" | "json";
  value: string;
};

export type HistoryChangeRow = {
  key: string;
  fieldPath: string;
  label: string;
  from: unknown;
  to: unknown;
};

export type HistoryDiffLine = {
  kind: "removed" | "added";
  prefix: "-" | "+";
  value: string;
};

const topLevelFieldLabels: Record<string, string> = {
  audiences: "Audiences",
  campaigns: "Campaigns",
  conclusion: "Conclusion",
  concluded_at: "Concluded at",
  control_campaign_id: "Assigned control",
  test_group: "Test group",
  created_at: "Created at",
  deleted: "Deleted",
  end_date: "End date",
  external_links: "External links",
  hypothesis: "Hypothesis",
  import_last_synced_at: "Import last synced at",
  import_metadata: "Import metadata",
  imported_global_experiment_id: "Imported global experiment",
  name: "Name",
  organizations: "Organizations",
  outcome: "Outcome",
  owner: "Owner",
  params: "Params",
  prediction_flights: "Prediction flights",
  rollout_additional_info: "Rollout additional info",
  rollout_at: "Rollout at",
  rollout_durability_ended_at: "Rollout ended at",
  rollout_durability_note: "Rollout durability note",
  rollout_durability_status: "Rollout durability",
  rollout_status: "Rollout status",
  scope_filters: "Scope filters",
  start_date: "Start date",
  traffic_allocation: "Traffic allocation",
  type: "Type",
  variant: "Variant",
};

const utcTimestampFields = new Set([
  "concluded_at",
  "created_at",
  "updated_at",
  "deleted_at",
  "import_last_synced_at",
  "rollout_at",
  "rollout_durability_ended_at",
]);

export function getVersionEventLabel(event: ExperimentVersion["event"]): string {
  switch (event) {
    case "create":
      return "Created";
    case "delete":
      return "Deleted";
    case "update":
      return "Updated";
    default:
      return event;
  }
}

export function formatHistoryFieldPath(fieldPath: string): string {
  const segments = splitFieldPath(fieldPath);
  return segments.map(formatFieldSegment).join(" / ");
}

export function formatHistoryValue(value: unknown, fieldPath?: string): FormattedHistoryValue {
  if (value === null || value === undefined || value === "") {
    return { kind: "empty", value: "empty" };
  }

  if (typeof value === "string" && fieldPath && isUTCTimestampField(fieldPath)) {
    return { kind: "text", value: formatUTCDateTime(value) };
  }

  if (typeof value === "string") {
    return { kind: "text", value };
  }

  if (typeof value === "number") {
    return { kind: "text", value: new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }).format(value) };
  }

  if (typeof value === "boolean") {
    return { kind: "text", value: value ? "true" : "false" };
  }

  return {
    kind: "json",
    value: JSON.stringify(value, null, 2),
  };
}

export function getHistoryChangeRows(version: ExperimentVersion): HistoryChangeRow[] {
  const rows: HistoryChangeRow[] = [];

  for (const change of version.changes || []) {
    const fields = change.changed_fields || [];
    for (const fieldPath of fields) {
      const fieldChange = change.object_changes?.[fieldPath];
      rows.push(buildHistoryChangeRow(change.id, fieldPath, fieldChange));
    }
  }

  return rows;
}

export function getHistoryDiffLines(change: HistoryChangeRow, event: ExperimentVersion["event"]): HistoryDiffLine[] {
  const lines: HistoryDiffLine[] = [];

  if (event !== "create") {
    lines.push(
      ...formatHistoryValueLines(change.from, change.fieldPath).map(value => ({
        kind: "removed" as const,
        prefix: "-" as const,
        value,
      })),
    );
  }

  if (event !== "delete") {
    lines.push(
      ...formatHistoryValueLines(change.to, change.fieldPath).map(value => ({
        kind: "added" as const,
        prefix: "+" as const,
        value,
      })),
    );
  }

  return lines;
}

function buildHistoryChangeRow(
  changeID: number,
  fieldPath: string,
  fieldChange?: ExperimentVersionFieldChange,
): HistoryChangeRow {
  return {
    key: `${changeID}-${fieldPath}`,
    fieldPath,
    label: formatHistoryFieldPath(fieldPath),
    from: fieldChange?.from,
    to: fieldChange?.to,
  };
}

function formatHistoryValueLines(value: unknown, fieldPath: string): string[] {
  const formatted = formatHistoryValue(value, fieldPath);
  return formatted.value.split("\n");
}

function splitFieldPath(fieldPath: string): string[] {
  const segments: string[] = [];
  let current = "";
  let bracketDepth = 0;

  for (const char of fieldPath) {
    if (char === "[") {
      bracketDepth += 1;
    } else if (char === "]") {
      bracketDepth -= 1;
    }

    if (char === "." && bracketDepth === 0) {
      segments.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  if (current) {
    segments.push(current);
  }

  return segments;
}

function formatFieldSegment(segment: string): string {
  const collectionMatch = /^(campaigns|prediction_flights)\[(.+)]$/.exec(segment);
  if (collectionMatch) {
    const [, collection, id] = collectionMatch;
    return collection === "campaigns" ? `Campaign ${id}` : `Prediction flight ${id}`;
  }

  if (/^\d+$/.test(segment)) {
    return titleizeSnakeCase(predictorTypeName(parseInt(segment, 10)));
  }

  return topLevelFieldLabels[segment] || titleizeSnakeCase(segment);
}

function isUTCTimestampField(fieldPath: string): boolean {
  const segments = splitFieldPath(fieldPath);
  const fieldName = segments[segments.length - 1];
  return utcTimestampFields.has(fieldName);
}

function titleizeSnakeCase(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
